import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, MapPin, Clock, Globe, CalendarPlus, X, RotateCcw, Settings } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { RescheduleDialog } from "../components/RescheduleDialog";
import { downloadAppointmentIcs } from "../lib/appointmentCal";
import { dateLocale } from "../lib/meta";

export default function ClientAppointments() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [appts, setAppts] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [resc, setResc] = useState(null);

  const load = () => api.get("/appointments/mine").then((r) => setAppts(r.data)).catch(() => setAppts([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm(t("booking.confirm_cancel"))) return;
    try { await api.patch(`/appointments/${id}/cancel`); toast.success(t("booking.cancelled_ok")); load(); }
    catch { toast.error(t("booking.cancel_err")); }
  };

  if (appts === null) return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;
  const now = new Date();
  const upcoming = appts.filter((a) => new Date(a.start_utc) >= now && a.status === "confirmed");
  const past = appts.filter((a) => new Date(a.start_utc) < now || ["cancelled", "completed", "no_show"].includes(a.status));
  const list = tab === "upcoming" ? upcoming : past;

  const fmt = (a) => new Date(a.start_utc).toLocaleString(dateLocale(lang), { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: a.client_timezone });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("booking.my_appts")}</h1>
        {user?.role === "client" && (
          <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="appts-settings-link">
            <Link to="/mi-cuenta"><Settings className="mr-1.5 h-3.5 w-3.5" />{t("booking.account_settings")}</Link>
          </Button>
        )}
      </div>
      <div className="mt-6 flex w-max gap-1 rounded-full border border-border bg-white p-1">
        {[["upcoming", t("booking.upcoming")], ["past", t("booking.past")]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`appts-tab-${v}`} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === v ? "bg-slateink text-white" : "text-slateink/70"}`}>{l}</button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="mt-6"><EmptyState icon={CalendarClock} title={t("booking.no_appts_client")}
          action={<Button asChild className="rounded-full bg-terracotta hover:bg-terracotta-hover"><Link to="/profesionales">{t("booking.find_professional")}</Link></Button>} testId="appts-empty" /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {list.map((a) => (
            <div key={a.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`appt-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <Link to={`/profesionales/${a.psychologist_id}`} className="font-display font-semibold text-slateink hover:text-terracotta">{a.psychologist_name}</Link>
                <StatusBadge status={a.status === "confirmed" ? "verified" : "pending"} label={t(`booking.st_${a.status}`)} />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{fmt(a)}</span>
                <span className="inline-flex items-center gap-1"><Globe className="h-4 w-4" />{a.client_timezone}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{t(`common.${a.modality === "in_person" ? "in_person" : a.modality}`)}</span>
              </div>
              {a.modality === "in_person" && a.location && <p className="mt-1 text-xs text-muted-foreground">{a.location}</p>}
              {a.modality === "online" && a.online_info && <p className="mt-1 text-xs text-muted-foreground">{a.online_info}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {tab === "upcoming" && a.status === "confirmed" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => downloadAppointmentIcs(a, { t, lang })} data-testid={`appt-cal-${a.id}`}><CalendarPlus className="mr-1.5 h-3.5 w-3.5" />{t("booking.add_to_calendar")}</Button>
                    <Button size="sm" variant="outline" onClick={() => setResc(a)} data-testid={`appt-reschedule-${a.id}`}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />{t("booking.reschedule")}</Button>
                    <Button size="sm" variant="outline" onClick={() => cancel(a.id)} data-testid={`appt-cancel-${a.id}`}><X className="mr-1.5 h-3.5 w-3.5" />{t("common.cancel")}</Button>
                  </>
                )}
                {tab === "past" && (
                  <Button asChild size="sm" variant="outline" data-testid={`appt-rebook-${a.id}`}><Link to={`/profesionales/${a.psychologist_id}?book=1&type=${a.type_id}`}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />{t("booking.book_again")}</Link></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <RescheduleDialog open={!!resc} onOpenChange={(o) => !o && setResc(null)} appt={resc} viewerTz={resc?.client_timezone} onDone={load} />
    </div>
  );
}
